import { supabase } from '../lib/supabase';
import type {
  AdminStats,
  ChartDataPoint,
  FunnelData,
  AdminUsersResponse,
  AdminUserDetail,
  AdminUsersFilter,
  AdminAIStats,
  AdminAIToolStats,
  AdminAIRecentConversation,
  AdminAIUsageChartPoint,
} from '../types/admin';

// Get admin dashboard stats
export async function getAdminStats(): Promise<AdminStats> {
  const { data, error } = await supabase.rpc('get_admin_stats');
  if (error) throw error;
  return data as AdminStats;
}

// Get user growth chart data
export async function getUserGrowthChart(daysBack: number = 30): Promise<ChartDataPoint[]> {
  const { data, error } = await supabase.rpc('get_user_growth_chart', { days_back: daysBack });
  if (error) throw error;
  return (data || []) as ChartDataPoint[];
}

// Get activity chart data
export async function getActivityChart(daysBack: number = 30): Promise<ChartDataPoint[]> {
  const { data, error } = await supabase.rpc('get_activity_chart', { days_back: daysBack });
  if (error) throw error;
  return (data || []) as ChartDataPoint[];
}

// Get funnel data
export async function getAdminFunnel(): Promise<FunnelData> {
  const { data, error } = await supabase.rpc('get_admin_funnel');
  if (error) throw error;
  return data as FunnelData;
}

// Get admin users list
export async function getAdminUsers(
  filters: AdminUsersFilter = {},
  limit: number = 50,
  offset: number = 0
): Promise<AdminUsersResponse> {
  const { data, error } = await supabase.rpc('get_admin_users', {
    p_search: filters.search || null,
    p_status: filters.status || null,
    p_role: filters.role || null,
    p_limit: limit,
    p_offset: offset,
  });
  if (error) throw error;
  return data as AdminUsersResponse;
}

// Get single user detail
export async function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  const { data, error } = await supabase.rpc('get_admin_user_detail', { p_user_id: userId });
  if (error) throw error;
  return data as AdminUserDetail;
}

// Toggle user suspension
export async function toggleUserSuspension(userId: string, suspend: boolean): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ is_suspended: suspend })
    .eq('id', userId);
  if (error) throw error;
}

// Update user role
export async function updateUserRole(userId: string, role: 'user' | 'admin'): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ role })
    .eq('id', userId);
  if (error) throw error;
}

// Update admin notes
export async function updateAdminNotes(userId: string, notes: string): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ admin_notes: notes })
    .eq('id', userId);
  if (error) throw error;
}

// Delete user
export async function deleteUser(userId: string): Promise<void> {
  const { data, error } = await supabase.rpc('admin_delete_user', { p_user_id: userId });
  if (error) throw error;
  if (!data) throw new Error('Failed to delete user');
}

// Get all products for admin
export async function getAdminProducts(
  search?: string,
  category?: string,
  productType?: string,
  limit: number = 50,
  offset: number = 0
) {
  let query = supabase
    .from('products')
    .select('*, product_media(id, url, type, sort_order)', { count: 'exact' })
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });

  if (search) {
    query = query.or(`name.ilike.%${search}%,sku.ilike.%${search}%`);
  }
  if (category) {
    query = query.eq('primary_category', category);
  }
  if (productType) {
    query = query.eq('product_type', productType);
  }

  query = query.range(offset, offset + limit - 1);

  const { data, error, count } = await query;
  if (error) throw error;
  return { products: data || [], total: count || 0 };
}

// Get single product with all relations for admin
export async function getAdminProduct(slug: string) {
  const { data, error } = await supabase
    .from('products')
    .select(`
      id, slug, name, sku, product_type, price_gross, base_price_value, base_price_unit,
      short_tagline, is_active, image_url, last_embedded_at,
      product_media(id, url, type, alt, sort_order),
      product_sections(id, section_type, title, content, sort_order),
      product_attributes(id, key, value, group_name, sort_order),
      product_documents(id, type, title, url, sort_order),
      bundle_items!bundle_items_bundle_product_id_fkey(id, child_product_id, child_product_name, quantity_text, sort_order)
    `)
    .eq('slug', slug)
    .single();
  if (error) throw error;
  return data;
}

// Update product
export async function updateProduct(
  productId: string,
  updates: {
    name?: string;
    price_gross?: number;
    base_price_value?: number;
    base_price_unit?: string;
    is_active?: boolean;
    short_tagline?: string;
  }
) {
  const { error } = await supabase
    .from('products')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', productId);
  if (error) throw error;
}

// Update product section content
export async function updateProductSection(
  sectionId: string,
  content: string,
  title?: string
) {
  const updates: { content: string; title?: string } = { content };
  if (title !== undefined) updates.title = title;

  const { error } = await supabase
    .from('product_sections')
    .update(updates)
    .eq('id', sectionId);
  if (error) throw error;
}

// Reorder product media
export async function reorderProductMedia(
  mediaItems: { id: string; sort_order: number }[]
) {
  for (const item of mediaItems) {
    const { error } = await supabase
      .from('product_media')
      .update({ sort_order: item.sort_order })
      .eq('id', item.id);
    if (error) throw error;
  }
}

// Add product media
export async function addProductMedia(
  productId: string,
  url: string,
  type: 'image' | 'video' | 'label' | 'nutrition_image' = 'image',
  alt?: string
) {
  // Get max sort_order
  const { data: existing } = await supabase
    .from('product_media')
    .select('sort_order')
    .eq('product_id', productId)
    .order('sort_order', { ascending: false })
    .limit(1);

  const sortOrder = existing && existing.length > 0 ? existing[0].sort_order + 1 : 0;

  const { error } = await supabase
    .from('product_media')
    .insert({ product_id: productId, url, type, alt, sort_order: sortOrder });
  if (error) throw error;
}

// Delete product media
export async function deleteProductMedia(mediaId: string) {
  const { error } = await supabase
    .from('product_media')
    .delete()
    .eq('id', mediaId);
  if (error) throw error;
}

// Get product categories for filter
export async function getProductCategories(): Promise<string[]> {
  const { data, error } = await supabase
    .from('products')
    .select('primary_category')
    .not('primary_category', 'is', null);
  if (error) throw error;

  const categories = [...new Set(data?.map(p => p.primary_category).filter(Boolean))];
  return categories.sort();
}

// ==========================================
// AI Agent Admin Functions
// ==========================================

// Get AI agent stats
export async function getAdminAIStats(): Promise<AdminAIStats> {
  const { data, error } = await supabase.rpc('get_admin_ai_stats');
  if (error) throw error;
  return data as AdminAIStats;
}

// Get AI tool call stats by action type
export async function getAdminAIToolStats(): Promise<AdminAIToolStats[]> {
  const { data, error } = await supabase.rpc('get_admin_ai_tool_stats');
  if (error) throw error;
  return (data || []) as AdminAIToolStats[];
}

// Get recent AI conversations
export async function getAdminAIRecentConversations(
  limit: number = 20
): Promise<AdminAIRecentConversation[]> {
  const { data, error } = await supabase.rpc('get_admin_ai_recent_conversations', {
    p_limit: limit,
  });
  if (error) throw error;
  return (data || []) as AdminAIRecentConversation[];
}

// Get AI usage chart data
export async function getAdminAIUsageChart(
  daysBack: number = 30
): Promise<AdminAIUsageChartPoint[]> {
  const { data, error } = await supabase.rpc('get_admin_ai_usage_chart', {
    days_back: daysBack,
  });
  if (error) throw error;
  return (data || []) as AdminAIUsageChartPoint[];
}

// ==========================================
// Product Knowledge Embedding Functions
// ==========================================

export interface EmbedProductResult {
  success: boolean;
  product_name?: string;
  embedded_count?: number;
  total_content_items?: number;
  error?: string;
}

// Embed product content for AI knowledge base
export async function embedProduct(productId: string): Promise<EmbedProductResult> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/embed-product`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ product_id: productId }),
    }
  );

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to embed product');
  }

  return result as EmbedProductResult;
}

// Get all product IDs for batch embedding
export async function getAllProductIds(): Promise<{ id: string; name: string }[]> {
  const { data, error } = await supabase
    .from('products')
    .select('id, name')
    .eq('is_active', true)
    .order('name', { ascending: true });

  if (error) throw error;
  return data || [];
}

// Get product embedding stats
export async function getProductEmbeddingStats(): Promise<{
  total_products: number;
  embedded_products: number;
  total_embeddings: number;
}> {
  const [productsRes, embeddingsRes] = await Promise.all([
    supabase.from('products').select('id, last_embedded_at', { count: 'exact' }),
    supabase.from('product_knowledge').select('id', { count: 'exact', head: true }),
  ]);

  const totalProducts = productsRes.count || 0;
  const embeddedProducts = productsRes.data?.filter(p => p.last_embedded_at).length || 0;
  const totalEmbeddings = embeddingsRes.count || 0;

  return {
    total_products: totalProducts,
    embedded_products: embeddedProducts,
    total_embeddings: totalEmbeddings,
  };
}
